import { useState, type ReactNode } from "react";
import {
  useFloating,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  autoUpdate,
  type Placement,
} from "@floating-ui/react";
import { twMerge } from "tailwind-merge";

// Architecture Note: This Dropdown intentionally does NOT wrap DropdownBase.
// Reason: This component is a generic container using @floating-ui/react for
// positioning ANY content, while DropdownBase is specifically designed for
// select-like dropdowns with predefined options and keyboard navigation.
// They serve different purposes:
// - Dropdown (this): Generic popover container for menus, tooltips, etc.
// - DropdownBase: Select-like component with options, value, onChange

export type Props = {
  /** Trigger element that opens the dropdown */
  trigger: ReactNode;
  /** Dropdown content */
  children: ReactNode;
  /** Additional CSS classes for the dropdown container */
  className?: string;
  /** Additional CSS classes for the trigger wrapper */
  triggerClassName?: string;
  /** Dropdown placement relative to trigger (defaults to "bottom") */
  placement?: Placement;
  /** Test ID for testing */
  "data-testid"?: string;
};

/**
 * Styled dropdown container component using @floating-ui/react for positioning.
 * Provides a generic dropdown that can contain any content.
 *
 * For a select-like dropdown with options, use DropdownBase from @core/ui.
 *
 * @example
 * <Dropdown trigger={<Button>Open Menu</Button>}>
 *   <div className="p-2">
 *     <button>Option 1</button>
 *     <button>Option 2</button>
 *   </div>
 * </Dropdown>
 */
export function Dropdown({
  trigger,
  children,
  className,
  triggerClassName,
  placement = "bottom",
  "data-testid": testId = "dropdown",
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    placement,
    strategy: "absolute",
    open: isOpen,
    onOpenChange: setIsOpen,
    // Re-measure on scroll, resize, and content-size changes so popovers
    // whose content grows (e.g. swapping a quick-bar for a full picker)
    // don't retain a stale position from the initial, smaller content.
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);

  const dismiss = useDismiss(context);

  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    role,
    dismiss,
  ]);

  return (
    <>
      <div
        ref={refs.setReference}
        className={twMerge("inline-block", triggerClassName)}
        {...getReferenceProps()}
        data-testid={testId}
      >
        {trigger}
      </div>

      {isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          onClick={() => setIsOpen(false)}
          className={twMerge(
            "z-50 min-w-[180px] max-w-sm rounded-md border border-brand-800 bg-brand-50 shadow-md overflow-hidden",
            className
          )}
          data-testid={`${testId}-menu`}
        >
          {children}
        </div>
      )}
    </>
  );
}

export default Dropdown;

